import React from "react";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { SERVICES } from "../data/services";
import { Shield, Clock3, MapPin, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { PG_PHONE, PG_PHONE_TEL, PG_EMAIL } from "../lib/api";

const TRUST_POINTS = [
  { icon: MapPin, text: "Serving all 5 NYC boroughs" },
  { icon: Clock3, text: "Same-day booking available" },
  { icon: Sparkles, text: "Response within minutes" },
  { icon: Shield, text: "Fully insured & bonded" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* HERO */}
      <section className="hero-dark" data-testid="hero-section">
        <div className="hero-overlay" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-24 md:pt-32 md:pb-40 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7 fade-up">
            <div className="eyebrow mb-6">Prime Globe Deep Cleaning LLC · NYC</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-white">
              Pristine spaces,<br />
              <span className="text-[#D4AF37]">booked in minutes.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#CBD5E1] max-w-xl leading-relaxed">
              Residential, commercial, and specialty cleaning across New York City. Transparent pricing,
              trusted crews, same-day availability.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/book"
                data-testid="hero-book-btn"
                className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-sm"
              >
                Start booking <ArrowRight size={18} />
              </Link>
              <a
                href={`tel:${PG_PHONE_TEL}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-sm border border-white/30 text-white hover:bg-white/10 transition text-sm font-medium"
              >
                Call {PG_PHONE}
              </a>
            </div>
          </div>

          <div className="md:col-span-5 fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="border border-[#1E293B] bg-[#0F172A]/70 backdrop-blur-sm p-7 rounded-sm">
              <div className="eyebrow mb-5">Why PRIMEGLOBE</div>
              <ul className="space-y-4">
                {TRUST_POINTS.map(({ icon: Icon, text }, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#F8FAFC]">
                    <span className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-[#D4AF37]/15 text-[#D4AF37]">
                      <Icon size={18} strokeWidth={1.75} />
                    </span>
                    <span className="text-sm">{text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-[#1E293B] grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="font-display text-3xl text-[#D4AF37]">50%</div>
                  <div className="text-xs text-[#94A3B8] mt-1">Deposit to confirm</div>
                </div>
                <div>
                  <div className="font-display text-3xl text-[#D4AF37]">13</div>
                  <div className="text-xs text-[#94A3B8] mt-1">Services available</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section id="services" className="section-pad bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow mb-4">Our services</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A1128]">
              A clean for every property in New York.
            </h2>
            <p className="mt-4 text-[#475569] text-base leading-relaxed">
              Select any service below to get an instant price estimate and crew availability.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s) => (
              <Link
                key={s.id}
                to={`/book?service=${s.id}`}
                data-testid={`service-card-${s.id}`}
                className="pg-card p-6 flex flex-col items-start gap-4 rounded-sm"
              >
                <span className="h-11 w-11 inline-flex items-center justify-center rounded-sm bg-[#F8FAFC] text-[#0A1128] group-hover:text-[#D4AF37]">
                  <i className={`fa-solid ${s.icon} text-lg`} />
                </span>
                <div>
                  <div className="font-display text-lg font-medium text-[#0A1128] tracking-tight">
                    {s.label}
                  </div>
                  <div className="text-sm text-[#64748B] mt-1.5 leading-relaxed">{s.blurb}</div>
                </div>
                <div className="mt-auto pt-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">
                  Book <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING + PROCESS */}
      <section id="pricing" className="section-pad bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="eyebrow mb-4">Transparent pricing</div>
            <h2 className="font-display text-3xl md:text-4xl font-light text-[#0A1128] tracking-tight">
              Instant quotes. No surprises.
            </h2>
            <p className="mt-4 text-[#475569] leading-relaxed">
              Our pricing engine calculates your estimate live as you configure your booking — based on
              property size, dirt level, add-ons, and urgency.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Live price breakdown",
                "Estimated crew size & hours",
                "50% deposit to confirm",
                "Remaining balance on completion",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-[#0A1128]">
                  <CheckCircle2 size={18} className="text-[#D4AF37]" strokeWidth={2} />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { title: "Apartment (1BR)", range: "$150 – $300", note: "Standard → Premium" },
                { title: "3BR House", range: "$340 – $850", note: "Standard → Premium" },
                { title: "Office (medium)", range: "$220 – $450", note: "Standard clean" },
                { title: "Pressure washing", range: "$300+", note: "By sqft" },
                { title: "Restaurant deep", range: "$700 – $1,500", note: "Full deep" },
                { title: "Window (per)", range: "$7 – $50", note: "By size/sides" },
              ].map((p) => (
                <div key={p.title} className="pg-card rounded-sm p-6">
                  <div className="text-xs uppercase tracking-[0.18em] text-[#94A3B8]">{p.note}</div>
                  <div className="font-display text-xl text-[#0A1128] mt-2">{p.title}</div>
                  <div className="font-display text-2xl text-[#D4AF37] mt-4">{p.range}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section id="trust" className="bg-[#0A1128] text-white py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {TRUST_POINTS.map(({ icon: Icon, text }, i) => (
            <div key={i} className="flex flex-col items-center gap-3">
              <Icon size={28} strokeWidth={1.5} className="text-[#D4AF37]" />
              <div className="text-sm text-[#CBD5E1]">{text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad bg-white text-center">
        <div className="max-w-3xl mx-auto">
          <div className="eyebrow mb-4">Ready?</div>
          <h2 className="font-display text-3xl md:text-5xl font-light text-[#0A1128] tracking-tight">
            Let’s make your space PRIMEGLOBE clean.
          </h2>
          <p className="mt-4 text-[#475569]">
            Build your booking in minutes, get an instant quote, and pay just 50% to confirm.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/book" data-testid="cta-book-btn" className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-sm">
              Book a cleaning <ArrowRight size={18} />
            </Link>
            <a
              href={`mailto:${PG_EMAIL}`}
              data-testid="cta-email-btn"
              className="btn-outline-navy inline-flex items-center gap-2 px-7 py-3.5 rounded-sm text-sm font-medium"
            >
              Email us
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
